import { useState } from 'react';

import { useTranslation } from '@tankef/i18n';
import { ShieldIcon } from '@tankef/icons';
import { Button } from '@tankef/ui';

import { useNotificationsContext } from '../notifications-context';

/**
 * Blocks the UI with a modal when there are unread mandatory notifications
 * (compliance/security) that require explicit acknowledgment before the
 * user can continue operating. Regulations (LFPDPPP, UIF) require the user
 * to confirm they've seen these notices.
 */
export function MandatoryNotificationGate() {
  const { t } = useTranslation('notifications');
  const { pendingMandatory, markAsRead } = useNotificationsContext();

  const [currentIndex, setCurrentIndex] = useState(0);

  if (pendingMandatory.length === 0) return null;

  // currentIndex resets to 0 on mount; pendingMandatory shrinks as items are acknowledged
  const safeIndex = Math.min(currentIndex, pendingMandatory.length - 1);
  const notification = pendingMandatory[safeIndex]!;
  const isLast = safeIndex === pendingMandatory.length - 1;
  const total = pendingMandatory.length;

  const handleAcknowledge = () => {
    markAsRead(notification.id);
    if (!isLast) {
      setCurrentIndex(safeIndex + 1);
    }
    // When last item is acknowledged, the context recomputes pendingMandatory → [] → gate unmounts
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mandatory-gate-title"
      className="fixed inset-0 z-top flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-tkf-surface p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-100">
            <ShieldIcon className="h-5 w-5 text-warning-600" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-warning-600">
              {t('mandatoryGate.badge')}
            </p>
            {total > 1 && (
              <p className="text-xs text-tkf-text-tertiary">
                {t('mandatoryGate.counter', { current: currentIndex + 1, total })}
              </p>
            )}
          </div>
        </div>

        {/* Content */}
        <h2
          id="mandatory-gate-title"
          className="mb-2 text-base font-semibold text-tkf-text-primary"
        >
          {notification.title}
        </h2>
        <p className="mb-6 text-sm text-tkf-text-secondary leading-relaxed">
          {notification.message}
        </p>

        {/* Action URL — show as secondary link */}
        {notification.action_url && (
          <a
            href={notification.action_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-4 block text-sm font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700"
          >
            {t('mandatoryGate.readMore')}
          </a>
        )}

        {/* CTA */}
        <Button variant="primary" size="md" className="w-full" onClick={handleAcknowledge}>
          {isLast ? t('mandatoryGate.confirm') : t('mandatoryGate.next')}
        </Button>

        <p className="mt-3 text-center text-xs text-tkf-text-tertiary">{t('mandatoryGate.hint')}</p>
      </div>
    </div>
  );
}
